import { AddHomeWorkRounded, MeetingRoom, PlayArrow } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import FlipIcon from '@mui/icons-material/Flip';
import LoopIcon from '@mui/icons-material/Loop';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DoorDrawerProps } from '../../../@types/ContextProviderRelated';
import { EOpeningDirection, EOrientation, EUnitType, EWall } from '../../../@types/Enums';
import { useAttributeActions } from '../../../hooks/useAttributeActions';
import { IWall } from '../../../models/abstract/Wall';
import AttributeModel, { IAttribute } from '../../../models/standard/AttributeModel';
import PhysicalBoundaryModel from '../../../models/standard/PhysicalBoundary';
import RoomModel from '../../../models/standard/RoomModel';
import { useBuilding } from '../../../provider/BuildingProvider/BuildingProvider';
import { usePlan } from '../../../provider/PlanProvider';
import { useUnitContext } from '../../../provider/UnitProvider';
import { getDefaultObjectId, ObjectType } from '../../../utils/attributes/defaultObjectsAndAttributes';
import { deleteButton, immersightBlue, immersightButton } from '../../../utils/colors';
import { MAX_DOOR_LENGTH, MAX_DOOR_LENGTH_IMPERIAL, MIN_DOOR_LENGTH, MIN_DOOR_LENGTH_IMPERIAL } from '../../../utils/constants';
import { cmToInternalRounded, getLocalizedUnitLabel, inchToInternalRounded, internalToCentimetersRounded, internalToInchesRounded } from '../../../utils/conversionUtils';
import filesApiService from '../../../utils/filesApiService';
import { WheelInputConfig } from '../../../utils/handleMouseEvents';
import { computeSpaceAreaInSquareMeters } from '../../../utils/multiroomUtils';
import AttributeAutocomplete from '../AttributeAutocomplete';
import AttributeList from '../AttributeList';

// --- HILFS-KOMPONENTE FÜR DAS HOVER/CLICK DER TÜR ---
const PreviewDoorItem = ({ d, isSelected, onSelect }: { d: any; isSelected: boolean; onSelect: (gIdx: number, dIdx: number) => void }) => {
  const [isHovered, setIsHovered] = useState(false);

  const strokeColor = isSelected ? '#E9B300' : isHovered ? '#E9B300' : '#3a3a3a';
  const boxFill = isSelected ? '#ffb703' : isHovered ? 'rgba(255, 183, 3, 0.4)' : 'white';
  const wedgeFill = isSelected ? 'rgba(255, 183, 3, 0.5)' : isHovered ? 'rgba(255, 183, 3, 0.15)' : 'transparent';

  return (
    <g onClick={() => onSelect(d.globalWallIdx, d.doorIdx)} style={{ cursor: 'pointer' }} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      <polygon points={d.boxPoly} fill="#ffffff" />

      <polygon points={d.boxPoly} fill={boxFill} stroke={strokeColor} strokeWidth={0.06} strokeLinejoin="miter" style={{ transition: 'all 0.2s ease' }} />

      <path
        d={`M ${d.hinge.x} ${d.hinge.y} L ${d.latch.x} ${d.latch.y} A ${d.width} ${d.width} 0 0 ${d.sweepFlag} ${d.leafEnd.x} ${d.leafEnd.y} Z`}
        fill={wedgeFill}
        style={{ transition: 'fill 0.2s ease' }}
      />

      <path
        d={`M ${d.latch.x} ${d.latch.y} A ${d.width} ${d.width} 0 0 ${d.sweepFlag} ${d.leafEnd.x} ${d.leafEnd.y}`}
        fill="none"
        stroke={strokeColor}
        strokeWidth={0.2}
        style={{ transition: 'stroke 0.2s ease' }}
      />

      <line
        x1={d.hinge.x}
        y1={d.hinge.y}
        x2={d.leafEnd.x}
        y2={d.leafEnd.y}
        stroke={strokeColor}
        strokeWidth={0.2}
        strokeLinecap="round"
        style={{ transition: 'stroke 0.2s ease' }}
      />

      <circle cx={(d.hinge.x + d.latch.x) / 2} cy={(d.hinge.y + d.latch.y) / 2} r={d.width} fill="transparent" />
    </g>
  );
};

// --- SUB-KOMPONENTE FÜR DIE RAUM-VORSCHAU ---
interface RoomPreviewProps {
  room: RoomModel;
  building: any;
  buildingWalls: IWall[];
  selectedDoor: { globalWallIdx: number; doorIdx: number } | null;
  onSelectDoor: (globalWallIdx: number, doorIdx: number) => void;
}

const RoomPreviewSVG: React.FC<RoomPreviewProps> = ({ room, building, buildingWalls, selectedDoor, onSelectDoor }) => {
  const previewData = useMemo(() => {
    const wallRoomMap = new Map<string, Set<number>>();
    building?.rooms?.forEach((r: any, rIdx: number) => {
      r.spaces?.forEach((s: any) => {
        s.boundaries?.forEach((b: any) => {
          if (b instanceof PhysicalBoundaryModel && Array.isArray(b.edges)) {
            b.edges.forEach((e: any) => {
              if (e.wall && e.wall.uuid) {
                if (!wallRoomMap.has(e.wall.uuid)) wallRoomMap.set(e.wall.uuid, new Set());
                wallRoomMap.get(e.wall.uuid)!.add(rIdx);
              }
            });
          }
        });
      });
    });

    const roomWallsMap = new Map<string, IWall>();
    room.spaces?.forEach((space) => {
      space.walls?.forEach((w) => roomWallsMap.set(w.uuid, w));
      space.boundaries?.forEach((b) => {
        if (b instanceof PhysicalBoundaryModel) {
          b.walls?.forEach((w) => roomWallsMap.set(w.uuid, w));
          b.edges?.forEach((edge) => {
            if (edge.wall?.uuid) roomWallsMap.set(edge.wall.uuid, edge.wall);
          });
        }
      });
    });

    const roomWalls = Array.from(roomWallsMap.values())
      .map((w) => buildingWalls.find((gw) => gw.uuid === w.uuid))
      .filter((w): w is IWall => w !== undefined);
    if (roomWalls.length === 0) return null;

    let minX = Infinity,
      maxX = -Infinity,
      minY = Infinity,
      maxY = -Infinity;
    roomWalls.forEach((w) => {
      minX = Math.min(minX, w.startPoint.x, w.endPoint.x);
      maxX = Math.max(maxX, w.startPoint.x, w.endPoint.x);
      minY = Math.min(minY, w.startPoint.y, w.endPoint.y);
      maxY = Math.max(maxY, w.startPoint.y, w.endPoint.y);
    });

    const pad = 2.5;
    const width = maxX - minX + pad * 2;
    const height = maxY - minY + pad * 2;

    const mapPt = (p: { x: number; y: number }) => ({
      x: p.x - minX + pad,
      y: maxY - p.y + pad,
    });

    const renderWalls: any[] = [];
    const renderDoors: any[] = [];

    roomWalls.forEach((w) => {
      const globalIdx = buildingWalls.findIndex((gw: any) => gw.uuid === w.uuid);
      const start = mapPt(w.startPoint);
      const end = mapPt(w.endPoint);
      const depth = (w as any).depth || 0.2;

      renderWalls.push({ id: w.uuid, start, end, depth });

      const isSharedWall = (wallRoomMap.get(w.uuid)?.size || 0) > 1;

      if (w.doors && w.doors.length > 0 && !isSharedWall) {
        w.doors.forEach((door: any, dIdx: number) => {
          const doorCenter = door.position
            ? mapPt(door.position)
            : {
                x: start.x + (end.x - start.x) * (door.t ?? 0.5),
                y: start.y + (end.y - start.y) * (door.t ?? 0.5),
              };

          const doorLength = door.length || 0.8;

          const dx = end.x - start.x;
          const dy = end.y - start.y;
          const len = Math.sqrt(dx * dx + dy * dy) || 1;
          const nx = dx / len;
          const ny = dy / len;

          const doorStart = { x: doorCenter.x - nx * (doorLength / 2), y: doorCenter.y - ny * (doorLength / 2) };
          const doorEnd = { x: doorCenter.x + nx * (doorLength / 2), y: doorCenter.y + ny * (doorLength / 2) };

          const normalNx = -ny;
          const normalNy = nx;

          const c1 = { x: doorStart.x + normalNx * (depth / 2), y: doorStart.y + normalNy * (depth / 2) };
          const c2 = { x: doorEnd.x + normalNx * (depth / 2), y: doorEnd.y + normalNy * (depth / 2) };
          const c3 = { x: doorEnd.x - normalNx * (depth / 2), y: doorEnd.y - normalNy * (depth / 2) };
          const c4 = { x: doorStart.x - normalNx * (depth / 2), y: doorStart.y - normalNy * (depth / 2) };

          const orientationMultiplier = door.orientation === EOrientation.OUTWARD ? -1 : 1;
          const swingNx = normalNx * orientationMultiplier;
          const swingNy = normalNy * orientationMultiplier;

          const isLeft = door.openingDirection === EOpeningDirection.LEFT;

          const lBase = isLeft ? doorStart : doorEnd;
          const hBase = isLeft ? doorEnd : doorStart;

          const hinge = { x: hBase.x + swingNx * (depth / 2), y: hBase.y + swingNy * (depth / 2) };
          const latch = { x: lBase.x + swingNx * (depth / 2), y: lBase.y + swingNy * (depth / 2) };

          const leafEnd = {
            x: hinge.x + swingNx * doorLength,
            y: hinge.y + swingNy * doorLength,
          };

          const v1x = latch.x - hinge.x;
          const v1y = latch.y - hinge.y;
          const v2x = leafEnd.x - hinge.x;
          const v2y = leafEnd.y - hinge.y;
          const cross = v1x * v2y - v1y * v2x;
          const sweepFlag = cross > 0 ? 1 : 0;

          renderDoors.push({
            globalWallIdx: globalIdx,
            doorIdx: dIdx,
            doorStart,
            doorEnd,
            boxPoly: `${c1.x},${c1.y} ${c2.x},${c2.y} ${c3.x},${c3.y} ${c4.x},${c4.y}`,
            hinge,
            latch,
            leafEnd,
            width: doorLength,
            depth: depth,
            sweepFlag,
          });
        });
      }
    });

    return { width, height, walls: renderWalls, doors: renderDoors };
  }, [room, buildingWalls, building]);

  if (!previewData) return null;

  return (
    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', bgcolor: '#ffffff', borderRadius: 2, p: 2, border: '1px solid #e0e0e0' }}>
      <svg viewBox={`0 0 ${previewData.width} ${previewData.height}`} style={{ width: '100%', maxHeight: '200px', overflow: 'visible' }}>
        {previewData.walls.map((w, i) => (
          <line key={`wall-${i}`} x1={w.start.x} y1={w.start.y} x2={w.end.x} y2={w.end.y} stroke="#3a3a3a" strokeWidth={w.depth} strokeLinecap="square" />
        ))}

        {previewData.doors.map((d, i) => {
          const isSelected = selectedDoor?.globalWallIdx === d.globalWallIdx && selectedDoor?.doorIdx === d.doorIdx;
          return <PreviewDoorItem key={`door-${i}`} d={d} isSelected={isSelected} onSelect={onSelectDoor} />;
        })}
      </svg>
    </Box>
  );
};

interface ConnectableRoomOption {
  id: string;
  roomIdx: number;
  title: string;
  area: number;
}

const DoorDrawer = ({ open, onClose, selectedDoor, resetSelection, id }: DoorDrawerProps): JSX.Element => {
  const {
    building,
    updateDoorWidth,
    deleteDoor,
    updateDoorOpeningDirection,
    updateDoorOrientation,
    updateDoorAttributes,
    addAdjacentRoomWallsFromDoor,
    connectRoomViaDoor,
    defaultObjects,
  } = useBuilding();

  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);
  const [selectedConnectionRoom, setSelectedConnectionRoom] = useState<ConnectableRoomOption | null>(null);
  const [selectedTargetDoor, setSelectedTargetDoor] = useState<{ globalWallIdx: number; doorIdx: number } | null>(null);

  const wallIndex = selectedDoor.wallIndex;
  const doorIndex = selectedDoor.doorIndex;
  const wall = building.walls[wallIndex] as IWall;
  const door = wall?.doors?.[doorIndex];

  const isPartition = wall?.type === EWall.PARTITION;

  const intl = useIntl();

  useEffect(() => {
    setSelectedTargetDoor(null);
  }, [selectedConnectionRoom]);

  // 1. Narrensicherer Check: Zähle die eindeutigen Räume, die diese Wand nutzen!
  const isCurrentWallShared = useMemo((): boolean => {
    if (!wall || !building || !building.rooms) return false;

    const connectedRoomIndices = new Set<number>();

    building.rooms.forEach((r, rIdx) => {
      r.spaces?.forEach((s) => {
        s.boundaries?.forEach((b) => {
          if (b instanceof PhysicalBoundaryModel && Array.isArray(b.edges)) {
            b.edges.forEach((e) => {
              if (e.wall && e.wall.uuid === wall.uuid) {
                connectedRoomIndices.add(rIdx);
              }
            });
          }
        });
      });
    });

    return connectedRoomIndices.size > 1;
  }, [building, wall]);

  const connectableRooms = useMemo(() => {
    if (!building || !building.rooms || !wall) return [];

    const wallUsageMap = new Map<string, Set<number>>();
    building.rooms.forEach((r, rIdx) => {
      r.spaces?.forEach((s) => {
        s.boundaries?.forEach((b) => {
          if (b instanceof PhysicalBoundaryModel) {
            b.edges?.forEach((e) => {
              if (e.wall?.uuid) {
                if (!wallUsageMap.has(e.wall.uuid)) wallUsageMap.set(e.wall.uuid, new Set());
                wallUsageMap.get(e.wall.uuid)!.add(rIdx);
              }
            });
          }
        });
      });
    });

    const roomsWithDoors: ConnectableRoomOption[] = [];
    const activeWallUuid = wall?.uuid;

    building.rooms.forEach((room: RoomModel, rIdx: number) => {
      let hasFreeDoor = false;
      let sharesActiveWall = false;
      let isRoomAlreadyConnected = false;
      let roomArea = 0;

      room.spaces?.forEach((space) => {
        roomArea += computeSpaceAreaInSquareMeters(space);

        const checkWall = (w: IWall) => {
          if (w.uuid === activeWallUuid) sharesActiveWall = true;

          const uniqueRoomsClaimingWall = wallUsageMap.get(w.uuid)?.size || 0;

          if (uniqueRoomsClaimingWall > 1) {
            isRoomAlreadyConnected = true;
          }

          const canonicalWall = building.walls.find((gw: IWall) => gw.uuid === w.uuid) ?? w;
          if (canonicalWall.doors && canonicalWall.doors.length > 0 && uniqueRoomsClaimingWall <= 1) {
            hasFreeDoor = true;
          }
        };

        space.walls?.forEach(checkWall);
        space.boundaries?.forEach((b) => {
          if (b instanceof PhysicalBoundaryModel) {
            b.walls?.forEach(checkWall);
            b.edges?.forEach((edge) => {
              if (edge.wall) checkWall(edge.wall);
            });
          }
        });
      });

      if (hasFreeDoor && !sharesActiveWall && !isRoomAlreadyConnected) {
        const nameFallback = intl.formatMessage({ id: 'WallDrawer.roomGeneric', defaultMessage: 'Raum {index}' }, { index: rIdx + 1 });
        roomsWithDoors.push({
          id: room.uuid || Math.random().toString(),
          roomIdx: rIdx,
          title: room.title?.trim() || nameFallback,
          area: roomArea,
        });
      }
    });

    return [{ id: 'none', roomIdx: -1, title: '', area: 0 }, ...roomsWithDoors];
  }, [building, wall, intl]);

  const handleConnectRooms = useCallback(() => {
    if (!selectedConnectionRoom || selectedConnectionRoom.id === 'none') return;
    if (!selectedTargetDoor) return;

    if (connectRoomViaDoor) {
      connectRoomViaDoor(selectedTargetDoor.globalWallIdx, selectedTargetDoor.doorIdx, wallIndex, doorIndex);
    }

    setIsConnectModalOpen(false);
    resetSelection();
    onClose();
  }, [selectedConnectionRoom, selectedTargetDoor, wallIndex, doorIndex, connectRoomViaDoor, resetSelection, onClose]);

  const { unitType } = useUnitContext();

  const initialDoorWidth = door?.length ? internalToCentimetersRounded(door.length) : MIN_DOOR_LENGTH;
  const initialDoorWidthImperial = door?.length ? internalToInchesRounded(door.length) : MIN_DOOR_LENGTH_IMPERIAL;

  const [doorWidth, setDoorWidth] = useState<number>(unitType === EUnitType.METRIC ? initialDoorWidth : initialDoorWidthImperial);

  const cmLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, 'centimeter'), [intl]);
  const inLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, 'inch'), [intl]);
  const { planModel } = usePlan();
  const regionCode = planModel?.region ? String(planModel.region).toUpperCase() : 'DE';

  const [attributes, setAttributes] = useState<AttributeModel[]>(door?.attributes ?? []);
  const [attributeValues, setAttributeValues] = useState<Record<number, string>>(
    (door?.attributes ?? []).reduce(
      (acc, a) => {
        if (a.value !== undefined && a.value !== null) acc[a.id] = `${a.value}`;
        return acc;
      },
      {} as Record<number, string>,
    ),
  );
  const [showInputFields, setShowInputFields] = useState<Record<number, boolean>>(
    (door?.attributes ?? []).reduce(
      (acc, a) => {
        acc[a.id] = a.value !== undefined && a.value !== null;
        return acc;
      },
      {} as Record<number, boolean>,
    ),
  );
  const [allAttributes, setAllAttributes] = useState<IAttribute[]>([]);
  const [attrSelected, setAttrSelected] = useState<IAttribute | null>(null);
  const [attrInputValue, setAttrInputValue] = useState('');
  const [attrError, setAttrError] = useState<string | null>(null);

  const { handleAttributeValueChange, handleDeleteAttribute, toggleInputField, handleAttributeSelection, getSortedAttributes } = useAttributeActions(
    attributes,
    setAttributes,
    setAttributeValues,
    setShowInputFields,
    setAttrSelected,
    setAttrInputValue,
    (attrs) => updateDoorAttributes(wallIndex, doorIndex, attrs),
    allAttributes,
    setAllAttributes,
    setAttrError,
  );

  const handleAttributeUnitChange = useCallback(
    (attrId: number, unit: string) => {
      const updated = attributes.map((a) =>
        a.id === attrId
          ? new AttributeModel(
              a.id,
              a.key,
              a.value,
              a.predecessorId,
              a.type,
              unit,
              a.hidden,
              a.forced,
              a.ownerId,
              a.userId,
              a.publicFlag,
              a.localization,
              a.defaults,
              a.validation,
              a.unitFamily,
            )
          : a,
      );
      setAttributes(updated);
      updateDoorAttributes(wallIndex, doorIndex, updated);
    },
    [attributes, updateDoorAttributes, wallIndex, doorIndex],
  );

  useEffect(() => {
    if (wall && door) {
      setDoorWidth(
        unitType === EUnitType.METRIC
          ? door.length
            ? internalToCentimetersRounded(door.length)
            : MIN_DOOR_LENGTH
          : door.length
            ? internalToInchesRounded(door.length)
            : MIN_DOOR_LENGTH_IMPERIAL,
      );
      setAttributes(door.attributes ?? []);
      setAttributeValues(
        (door.attributes ?? []).reduce(
          (acc, a) => {
            if (a.value !== undefined && a.value !== null) acc[a.id] = `${a.value}`;
            return acc;
          },
          {} as Record<number, string>,
        ),
      );
      setShowInputFields(
        (door.attributes ?? []).reduce(
          (acc, a) => {
            acc[a.id] = a.value !== undefined && a.value !== null;
            return acc;
          },
          {} as Record<number, boolean>,
        ),
      );
    } else {
      onClose();
    }
  }, [doorIndex, wallIndex, unitType, door, wall, onClose]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const catalog = await filesApiService.getAttributes(regionCode);
        setAllAttributes(catalog);
        setAttrError(null);
      } catch (e) {
        setAttrError(`Error: ${(e as Error).message}`);
      }
    })();
  }, [open, regionCode]);

  const validateAndSet = useCallback(
    (value: number): void => {
      const minValue = unitType === EUnitType.METRIC ? MIN_DOOR_LENGTH : MIN_DOOR_LENGTH_IMPERIAL;
      const maxValue = unitType === EUnitType.METRIC ? MAX_DOOR_LENGTH : MAX_DOOR_LENGTH_IMPERIAL;

      if (!isNaN(value)) {
        if (value < minValue) value = minValue;
        else if (value > maxValue) value = maxValue;
        const roundedInternalValue = unitType === EUnitType.METRIC ? cmToInternalRounded(value) : inchToInternalRounded(value);
        setDoorWidth(value);
        updateDoorWidth(wallIndex, doorIndex, roundedInternalValue);
      } else {
        setDoorWidth(unitType === EUnitType.METRIC ? internalToCentimetersRounded(door?.length || 0) : internalToInchesRounded(door?.length || 0));
      }
    },
    [unitType, door, updateDoorWidth, wallIndex, doorIndex],
  );

  const localizedDoorName = useMemo(() => {
    const doorObjId = getDefaultObjectId(ObjectType.Door);
    const apiObj = defaultObjects?.find((o: any) => o.id === doorObjId);
    if (apiObj && apiObj.localization) {
      const currentLocale = intl.locale.split('-')[0];
      return apiObj.localization[currentLocale] || apiObj.localization.en || apiObj.name;
    }
    return intl.formatMessage({ id: 'DoorDrawer.door', defaultMessage: 'Tür' });
  }, [defaultObjects, intl]);

  const handleCloseDrawer = useCallback(() => {
    resetSelection();
    onClose();
  }, [onClose, resetSelection]);

  const handleOnDelete = useCallback(() => {
    deleteDoor(wallIndex, doorIndex);
    resetSelection();
    onClose();
  }, [wallIndex, doorIndex, deleteDoor, resetSelection, onClose]);

  const wheelConfig: WheelInputConfig = {
    unitType,
    minMetric: MIN_DOOR_LENGTH,
    maxMetric: MAX_DOOR_LENGTH,
    minInches: MIN_DOOR_LENGTH_IMPERIAL,
    maxInches: MAX_DOOR_LENGTH_IMPERIAL,
  };

  if (!wall) return null;

  return (
    <>
      <Drawer
        key={id}
        anchor="right"
        open={open}
        variant={open ? 'permanent' : 'temporary'}
        sx={{ '& .MuiPaper-root': { top: '64px', zIndex: 1000, width: '420px', height: 'calc(100% - 64px)' } }}
      >
        <Grid container direction="column" p={0}>
          <Grid item sx={{ backgroundColor: immersightBlue, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 0.5 }}>
            <Box sx={{ marginLeft: '16px' }}>
              <Typography variant="h6" sx={{ textTransform: 'uppercase' }}>
                {localizedDoorName}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseDrawer} sx={{ color: '#fff', '&:hover': { backgroundColor: 'transparent' } }}>
              <PlayArrow sx={{ fontSize: 30 }} />
            </IconButton>
          </Grid>
        </Grid>

        <Grid container item direction="column" spacing={3} p={3}>
          <Grid item>
            <Typography variant="subtitle2" gutterBottom style={{ color: '#4a4a4a', letterSpacing: '0.5px' }}>
              <FormattedMessage id="DoorDrawer.doorWidth" values={{ unit: unitType === EUnitType.METRIC ? cmLocalizedLabel : inLocalizedLabel }} />
            </Typography>
            <TextField
              fullWidth
              type="number"
              value={doorWidth}
              onChange={(e) => setDoorWidth(parseInt(e.target.value, 10))}
              onBlur={(e) => validateAndSet(parseInt(e.target.value, 10))}
              // ---> NEU: onKeyDown Handler hinzufügen <---
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  validateAndSet(parseInt((e.target as HTMLInputElement).value, 10));
                  // Optional: Fokus vom Textfeld nehmen, um die Änderung visuell abzuschließen
                  (e.target as HTMLInputElement).blur();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SyncAltIcon />
                  </InputAdornment>
                ),
                inputProps: {
                  min: unitType === EUnitType.METRIC ? MIN_DOOR_LENGTH : MIN_DOOR_LENGTH_IMPERIAL,
                  max: unitType === EUnitType.METRIC ? MAX_DOOR_LENGTH : MAX_DOOR_LENGTH_IMPERIAL,
                },
              }}
            />
          </Grid>

          <Grid item>
            <AttributeList
              attributes={getSortedAttributes(attributes)}
              attributeValues={attributeValues}
              showInputFields={showInputFields}
              onAttributeValueChange={handleAttributeValueChange}
              onAttributeUnitChange={handleAttributeUnitChange}
              onDeleteAttribute={handleDeleteAttribute}
              onToggleInputField={toggleInputField}
              onReorderAttributes={(newAttrs) => {
                setAttributes(newAttrs);
                updateDoorAttributes(wallIndex, doorIndex, newAttrs);
              }}
            />
          </Grid>

          <Grid item>
            <Box sx={{ mt: -5, mb: 1, width: '99%', mx: 'auto' }}>
              <AttributeAutocomplete
                attributes={allAttributes}
                disabledAttributeIds={attributes.map((attribute) => attribute.id)}
                selected={attrSelected}
                inputValue={attrInputValue}
                error={attrError}
                onChange={handleAttributeSelection}
                onInputChange={(val) => {
                  setAttrInputValue(val);
                  if (val !== attrSelected?.key) setAttrSelected(null);
                }}
                placeholder={intl.formatMessage({ id: 'DoorDrawer.addAttribute', defaultMessage: 'Add attribute' })}
              />
            </Box>
          </Grid>

          <Grid item>
            <Button variant="contained" startIcon={<LoopIcon />} onClick={() => updateDoorOpeningDirection(wallIndex, doorIndex)} sx={{ backgroundColor: immersightButton }}>
              <FormattedMessage id="DoorDrawer.doorOpeningDirection" />
            </Button>
          </Grid>

          <Grid item>
            <Button variant="contained" startIcon={<FlipIcon />} onClick={() => updateDoorOrientation(wallIndex, doorIndex)} sx={{ backgroundColor: immersightButton }}>
              <FormattedMessage id="DoorDrawer.doorOrientation" />
            </Button>
          </Grid>

          {!isCurrentWallShared && connectableRooms.length > 1 && (
            <Grid item>
              <Button variant="contained" startIcon={<MeetingRoom />} onClick={() => setIsConnectModalOpen(true)} sx={{ backgroundColor: immersightButton }}>
                Türen verbinden
              </Button>
            </Grid>
          )}

          {!isCurrentWallShared && !isPartition && (
            <Grid item>
              <Button
                variant="contained"
                startIcon={<AddHomeWorkRounded />}
                onClick={() => addAdjacentRoomWallsFromDoor(wall.uuid, doorIndex)}
                sx={{ backgroundColor: immersightButton }}
              >
                <FormattedMessage id="DoorDrawer.addRoom" />
              </Button>
            </Grid>
          )}

          <Grid item>
            <Button variant="contained" startIcon={<DeleteIcon />} onClick={handleOnDelete} sx={{ backgroundColor: deleteButton, marginTop: '20px' }}>
              <FormattedMessage id="DoorDrawer.deleteDoor" />
            </Button>
          </Grid>
        </Grid>
      </Drawer>

      <Dialog
        open={isConnectModalOpen}
        onClose={() => setIsConnectModalOpen(false)}
        PaperProps={{ sx: { borderRadius: '16px', width: '500px', boxShadow: '0px 8px 16px rgba(0,0,0,0.15)' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1.5, pt: 2 }}>
          <Typography variant="h6" component="span" sx={{ color: '#464646', fontWeight: 500 }}>
            Türen verbinden
          </Typography>
          <IconButton onClick={() => setIsConnectModalOpen(false)} size="small" sx={{ color: '#464646' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Divider sx={{ mx: 0 }} />

        <DialogContent sx={{ pt: 2, pb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1, color: '#464646', fontWeight: 500 }}>
            Wähle den Raum mit welchem du die Türe verbinden möchtest.
          </Typography>
          <Autocomplete
            options={connectableRooms}
            getOptionLabel={(option) => (option.id === 'none' ? option.title : `${option.title} (${option.area.toFixed(2)} m²)`)}
            value={selectedConnectionRoom || connectableRooms[0]}
            onChange={(_, newValue) => setSelectedConnectionRoom(newValue)}
            disableClearable
            renderInput={(params) => (
              <TextField
                {...params}
                label="Raum auswählen"
                variant="outlined"
                InputLabelProps={{ shrink: true, sx: { backgroundColor: '#fff', px: 0.5, color: '#464646' } }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', backgroundColor: '#fff' } }}
              />
            )}
            PaperComponent={(props) => <Paper {...props} sx={{ borderRadius: '12px', mt: 1, boxShadow: '0px 4px 12px rgba(0,0,0,0.15)', bgcolor: '#fff' }} />}
          />

          {selectedConnectionRoom && selectedConnectionRoom.id !== 'none' && building.rooms && (
            <>
              <Typography variant="body2" sx={{ mt: 3, color: '#464646', fontWeight: 500 }}>
                Wähle aus welche Türe du verbinden möchtest.
              </Typography>

              <RoomPreviewSVG
                room={building.rooms[selectedConnectionRoom.roomIdx]}
                building={building}
                buildingWalls={building.walls}
                selectedDoor={selectedTargetDoor}
                onSelectDoor={(gIdx, dIdx) => setSelectedTargetDoor({ globalWallIdx: gIdx, doorIdx: dIdx })}
              />

              <Typography variant="caption" sx={{ display: 'block', mt: 3, color: '#888', lineHeight: 1.4 }}>
                Beim Zusammenführen von zwei Räumen bleibt der Raum bestehen, von dessen Tür aus gestartet wurde. Die Raumform des zweiten Raums wird dabei gegebenenfalls
                angepasst. Fenster oder Türen an überlappenden Wänden können verloren gehen und müssen ggf. manuell neu angelegt werden.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, pt: 0 }}>
          <Button
            onClick={handleConnectRooms}
            variant="contained"
            fullWidth
            disabled={!selectedConnectionRoom || selectedConnectionRoom.id === 'none' || !selectedTargetDoor}
            sx={{
              backgroundColor: immersightButton,
              borderRadius: '8px',
              py: 1,
              '&:disabled': { backgroundColor: '#d3d3d3', color: '#fff' },
            }}
          >
            TÜR VERBINDEN
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DoorDrawer;
